<template>
  <div>
    <strong>{{ $t('package') }}</strong>
    <div>
      <p>
        {{ $t('attrs.parcel_type') }}:
        <strong v-if="['box', 'doc', 'ltl'].indexOf(parcel.parcel_type) > -1">{{ $t(`list.parcelTypes.${parcel.parcel_type}`) }}</strong>
        <strong v-else>{{ $t('package') }}</strong>
      </p>

      <div v-if="parcel.parcel_type !== 'doc'">
        <span>
          {{ $t('attrs.length') }}:
          <strong>{{ parcel.length }} {{ parcel.distance_unit }}</strong>
        </span>

        <span class="ml-1">
          {{ $t('attrs.height') }}:
          <strong>{{ parcel.height }} {{ parcel.distance_unit }}</strong>
        </span>

        <br />
        <span>
          {{ $t('attrs.width') }}:
          <strong>{{ parcel.width }} {{ parcel.distance_unit }}</strong>
        </span>

        <span class="ml-1">
          {{ $t('attrs.weight') }}:
          <strong>{{ parcel.weight }} {{ parcel.mass_unit }}</strong>
        </span>

        <br />
        <span>
          {{ $t('declaredTaxValue') }}:
          <strong>{{ parcel.declared_tax_value | currency }}</strong>
        </span>
        <br />
      </div>

      <span>
        {{ $t('attrs.contents') }}:
        <strong>{{ parcel.contents }}</strong>
      </span>

      <br />
      <span>
        {{ $t('attrs.sat_product_id') }}:
        <strong>{{ parcel.sat_product_id }}</strong>
      </span>

      <br />
      <span>
        {{ $t('volumetricWeight') }}:
        <strong>{{ volumetricWeight }}</strong>
      </span>
    </div>

    <v-btn color="primary" dark small absolute bottom right fab v-if="editable" @click="edit">
      <v-icon>edit</v-icon>
    </v-btn>

    <v-layout row justify-center v-if="editable">
      <v-dialog v-model="dialog" persistent max-width="600px">
        <v-card>
          <v-card-title>
            <v-container fluid grid-list-xs>
              <v-layout row wrap>
                <v-flex xs12>
                  <h1 class="title">
                    {{ $t('package') }}
                  </h1>
                </v-flex>

                <v-flex xs12>
                  <v-select :items="packages"
                            item-value="id"
                            item-text="name"
                            :label="$t('templates')"
                            v-on:change="setParcel"
                            return-object
                  ></v-select>
                </v-flex>

                <v-flex xs12>
                  <v-select v-model="parcel.parcel_type"
                            :items="parcelTypes"
                            item-value="id"
                            item-text="name"
                            :label="$t('attrs.parcel_type')"
                            ></v-select>
                </v-flex>

                <v-flex xs6>
                  <v-text-field v-model="parcel.declared_tax_value"
                                    name="declaredValue"
                                    :label="$t('declaredTaxValue')"
                                    v-validate="'required|decimal'"
                                    :error-messages="errors.collect('declaredValue')"
                                    data-vv-name="declaredValue"
                                    :data-vv-as="$t('declaredTaxValue')"
                                    required
                                    ></v-text-field>
                </v-flex>

                <v-flex xs6>
                  <v-autocomplete
                    v-model="parcel.sat_product_id"
                    :items="satProducts"
                    :search-input.sync="satProductSearch"
                    hide-no-data
                    hide-selected
                    item-text="description"
                    item-value="code"
                    :label="$t('attrs.sat_product_id')"
                    :placeholder="$t('attrs.sat_product_id')"
                  ></v-autocomplete>
                </v-flex>

                <v-flex xs12 v-if="parcel.parcel_type !== 'doc'">
                  <v-layout row wrap>
                    <v-flex xs12 md3>
                      <v-text-field v-model="parcel.length"
                                    name="length"
                                    :label="$t('attrs.length')"
                                    v-validate="'required|decimal|min_value:1'"
                                    :error-messages="errors.collect('length')"
                                    data-vv-name="length"
                                    :data-vv-as="$t('attrs.length')"
                                    :readonly="!parcel.editable"
                                    required
                                    ></v-text-field>
                    </v-flex>
                    <v-flex xs12 md3>
                      <v-text-field v-model="parcel.width"
                                    name="width"
                                    :label="$t('attrs.width')"
                                    v-validate="'required|decimal|min_value:1'"
                                    :error-messages="errors.collect('width')"
                                    data-vv-name="width"
                                    :data-vv-as="$t('attrs.width')"
                                    :readonly="!parcel.editable"
                                    required
                                    ></v-text-field>
                    </v-flex>
                    <v-flex xs12 md3>
                      <v-text-field v-model="parcel.height"
                                    name="height"
                                    :label="$t('attrs.height')"
                                    v-validate="'required|decimal|min_value:1'"
                                    :error-messages="errors.collect('height')"
                                    data-vv-name="height"
                                    :data-vv-as="$t('attrs.height')"
                                    :readonly="!parcel.editable"
                                    required
                                    ></v-text-field>
                    </v-flex>
                    <v-flex xs12 md3>
                      <v-select v-model="parcel.distance_unit"
                                :items="distanceUnits"
                                item-value="id"
                                item-text="name"
                                :label="$t('attrs.distance_unit')"
                                :readonly="!parcel.editable"
                                ></v-select>
                    </v-flex>

                    <v-flex xs12 md3>
                      <v-text-field v-model="parcel.weight"
                                    name="weight"
                                    :label="$t('attrs.weight')"
                                    v-validate="'required|decimal|min_value:0.1'"
                                    :error-messages="errors.collect('weight')"
                                    data-vv-name="weight"
                                    :data-vv-as="$t('attrs.weight')"
                                    :readonly="!parcel.editable"
                                    required
                                    ></v-text-field>
                    </v-flex>
                    <v-flex xs12 md3>
                      <v-select v-model="parcel.mass_unit"
                                :items="massUnits"
                                item-value="id"
                                item-text="name"
                                :label="$t('attrs.mass_unit')"
                                :readonly="!parcel.editable"
                                ></v-select>
                    </v-flex>
                  </v-layout>
                </v-flex>

                <v-flex xs12 md12>
                  <v-text-field v-model="parcel.contents"
                    name="contents"
                    :label="$t('attrs.contents')"
                    v-validate="'required'"
                    :error-messages="errors.collect('contents')"
                    data-vv-name="contents"
                    :data-vv-as="$t('attrs.contents')"
                    :readonly="!parcel.editable"
                    required
                  ></v-text-field>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-title>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" flat @click="dialog = false" :loading="loading" :disabled="loading">{{ $t('cancel') }}</v-btn>
            <v-btn color="primary" dark @click="save"  :loading="loading" :disabled="loading" type="submit">{{ $t('save') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-layout>
  </div>
</template>

<script>
import axios from 'axios'

import Parcel from '@/client/parcel'
import UserPackage from '@/client/user-package'

export default {
  name: 'ParcelInfoComp',

  props: {
    shipment: {
      type: Object
    },

    editable: {
      type: Boolean,
      default: false
    },

    volumetricWeight: String
  },

  data () {
    return {
      loading: false,
      dialog: false,
      parcel: {},
      package: {},
      parcelTypes:[
        { id: 'box', name: this.$t('list.parcelTypes.box') },
        { id: 'doc', name: this.$t('list.parcelTypes.doc') },
        { id: 'ltl', name: this.$t('list.parcelTypes.ltl') }
      ],
      distanceUnits: [
        { id: 'cm', name: 'CM' }
      ],
      massUnits: [
        { id: 'kg', name: 'KG' }
      ],
      packages: [],
      satProducts: [],
      satProductSearch: ''
    }
  },

  components: {
  },

  computed: {
  },

  methods: {
    edit () {
      this.dialog = true
    },

    async save () {
      this.parcel.contents = this.parcel.contents.spanishize()

      const isValid = await this.$validator.validateAll()
      if (!isValid) { return }

      this.loading = true
      try {
        Parcel.shipmentId = this.shipment.id
        this.parcel.shipment_id = this.shipment.id
        await this.parcel.save()
        this.dialog = false
        this.$emit('fetch')
      } catch (err) {
        this.handleErr(err)
      }
      this.loading = false
    },

    async searchSatProduct (val) {
      const url = 'https://sat.api.hydraship.app/api/products'
      const res = await axios.get(url, { params: { search: val } })
      this.satProducts = res.data.data
    },

    setParcel(selectedPackage) {
      this.parcel.parcel_type = selectedPackage.package_type || 'box'
      this.parcel.distance_unit = selectedPackage.distance_unit
      this.parcel.mass_unit = selectedPackage.mass_unit
      this.parcel.weight = selectedPackage.weight
      this.parcel.length = selectedPackage.length
      this.parcel.height = selectedPackage.height
      this.parcel.width = selectedPackage.width
      this.parcel.contents = selectedPackage.contents
      this.parcel.sat_product_id = selectedPackage.sat_product_id
      this.parcel.declared_tax_value = selectedPackage.declared_tax_value || 0
    }
  },

  watch: {
    satProductSearch(val) {
      this.searchSatProduct(val)
    },

    shipment () {
      this.parcel = new Parcel(this.shipment.parcels[0])
    }
  },

  async mounted () {
    const parc = this.shipment && this.shipment.parcels && this.shipment.parcels[0]
    if (parc) {
      this.parcel = new Parcel(parc)
    } else {
      this.parcel = new Parcel()
    }

    if (this.parcel.parcel_type === 'package') {
      this.parcel.parcel_type = this.parcel.package_id
      this.parcel.editable = false
    }
    else {
      this.parcel.editable = true
    }

    UserPackage.shipmentId = this.shipment.id
    this.packages = await UserPackage.all()
  }
}
</script>
